import { Formik, Field, Form, FormikHelpers } from 'formik';
import queryString from 'query-string';
import React, { FunctionComponent, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link, Prompt, useLocation } from 'react-router-dom';
import { AutoSizer, List } from 'react-virtualized';
import styled from 'styled-components';

import ProductModifierGroupListItem from './product-modifier-group-list-item/product-modifier-group-list-item.component';

import { IQueryParams } from 'app.types';
import brand from 'assets/styles/variables/brand';
import fonts from 'assets/styles/variables/fonts';
import { addAlert } from 'components/alert/alert.slice';
import Button from 'components/button/button.component';
import Checkbox from 'components/form-inputs/checkbox/checkbox.component';
import Input from 'components/form-inputs/input/input.component';
import SortIcons from 'components/sort-icons/sort-icons.component';
import { useReduxDispatch } from 'helpers/use-redux-dispatch.helper';
import { intl } from 'modules/core/i18n/i18n.config';
import { RootState } from 'modules/core/state/root.reducer';
import {
	getModifierGroupsList,
	patchProductDetails,
} from 'modules/menu/menu.slice';
import { IModifierGroup } from 'modules/menu/menu.types';

interface IFormValues {
	[index: string]: boolean;
}

// Interface for list item props
interface IListItemProps {
	key: string;
	index: number;
	isScrolling: boolean;
	style: Object;
}

const StyledForm = styled(Form)`
	width: 100%;
	display: flex;
	flex-direction: column;
`;

const StyledHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	margin: 0 0 20px;

	h2 {
		margin: 0;
	}
`;

const StyledInput = styled(Input)`
	margin: 0;
	width: 100%;
	max-width: 160px;

	input {
		height: 35px;
		font-size: ${fonts.sizes.small};
		background: ${brand.neutral};
	}
`;

const StyledCheckbox = styled(Checkbox)`
	margin: 0 20px 0 0;
`;

const StyledFilterForm = styled(Form)`
	display: flex;
	align-items: center;
`;

const StyledSortIcons = styled(SortIcons)`
	margin: 0 10px 0 0;
`;

const StyledHeadings = styled.div`
	display: flex;
	align-items: center;
	font-size: ${fonts.sizes.med};
	line-height: ${fonts.line_height.large};
	user-select: none;
	padding: 0 15px;
`;

const StyledHeading = styled.div`
	display: flex;
	align-items: center;
	min-width: 120px;
	margin: 0 45px 0 0;
	user-select: none;

	&.mod-sort {
		cursor: pointer;
	}

	:focus {
		outline: none;
	}

	&:first-child {
		min-width: 30px;
		width: 30px;
		margin-right: 15px;
	}

	&:nth-child(2),
	&:nth-child(3) {
		width: calc(50% - 80px);
		max-width: 300px;
	}
`;

const StyledList = styled.div`
	width: 100%;
	height: 550px;
	user-select: none;
	display: block;

	.ReactVirtualized__List:focus {
		outline: none;
	}
`;

const StyledActions = styled.div`
	margin-top: 10px;
	padding-top: 20px;
	border-top: 1px solid ${brand.borders};
	display: flex;
	align-items: center;
	justify-content: space-between;

	Button:first-child {
		margin-right: 20px;
	}
`;

interface IComponentProps {
	productId: string;
}

const ProductModifierGroupsForm: FunctionComponent<IComponentProps> = ({
	productId,
}) => {
	// Get redux dispatch
	const dispatch = useReduxDispatch();
	const location = useLocation();

	// Variable for when form is submitted
	const [formSubmission, setFormSubmission] = useState(false);

	// Set use state for sort, search and show selected
	const [sortDirection, setSortDirection] = useState('');
	const [sortColumn, setSortColumn] = useState<keyof IModifierGroup>('title');
	const [searchQuery, setSearchQuery] = useState('');
	const [showSelected, setShowSelected] = useState(false);

	// Get query params
	const query: IQueryParams = queryString.parse(location.search);

	// Get active account ID from state
	const { activeAccountId } = useSelector((state: RootState) => state.auth);

	// Get active product details from store
	const productDetails = useSelector(
		(state: RootState) => state.menu.activeProductDetails
	);

	// Get all modifierGroups from state
	let modifierGroups =
		useSelector((state: RootState) => state.menu.modifierGroups) || [];

	// If we have products, filter and sort
	if (modifierGroups.length > 0) {
		modifierGroups = modifierGroups
			.filter(
				(modifierGroup) =>
					(modifierGroup.title &&
						modifierGroup.title
							.toLowerCase()
							.includes(searchQuery.toLowerCase())) ||
					(modifierGroup.sku &&
						modifierGroup.sku
							.toLowerCase()
							.includes(searchQuery.toLowerCase()))
			)
			.sort((a, b) => {
				const sortA = a[sortColumn];
				const sortB = b[sortColumn];
				if (sortDirection === 'ASC') {
					return sortA! > sortB! ? 1 : -1;
				}
				if (sortDirection === 'DESC') {
					return sortA! > sortB! ? -1 : 1;
				}

				return 0;
			});
	}
	// Convert products to initial values
	const currentProductModifierGroups = productDetails?.modifierGroups?.reduce(
		(allModifierGroups, modifierGroup) => ({
			...allModifierGroups,
			[modifierGroup]: true,
		}),
		{}
	);

	// Initial form values
	const initialValues: IFormValues = currentProductModifierGroups!;

	useEffect(() => {
		// Get modifier groups list
		const getData = async (id?: string) => {
			dispatch(getModifierGroupsList({ pageSize: -1 }));
		};

		getData(productId);
	}, [dispatch, productId]);

	// Handle form submission
	const handleSubmit = async (
		values: IFormValues,
		{ setSubmitting }: FormikHelpers<IFormValues>
	) => {
		// Set formik submission state to true
		setSubmitting(true);

		// Create array of empty modifier groups
		const updatedModifierGroups: string[] = [];

		// Filter values if they're set to true
		for (const key in values) {
			if (values[key]) {
				updatedModifierGroups.push(key);
			}
		}

		// Send patch request
		const response = await dispatch(
			patchProductDetails(productId, {
				modifierGroups: updatedModifierGroups,
			})
		);

		// Return on fail
		if (!response) {
			// Set formik submission state to false
			setSubmitting(false);
			return;
		}

		// Show success alert
		await dispatch(
			addAlert({
				title: intl.formatMessage({
					id: 'alerts.success.title',
				}),
				message: intl.formatMessage({
					id: 'productForm.alerts.updated.message',
				}),
				type: 'success',
			})
		);

		// Set formik submission state to false
		setSubmitting(false);

		// Set for submission to true to remove routing prompt
		setFormSubmission(true);
	};

	const setSort = (column: keyof IModifierGroup) => {
		let sort;
		if (sortDirection === 'ASC') {
			sort = 'DESC';
		} else if (sortDirection === 'DESC') {
			sort = '';
		} else {
			sort = 'ASC';
		}

		setSortDirection(sort);
		setSortColumn(column);
	};

	return (
		<>
			<StyledHeader>
				<h2>
					<FormattedMessage
						id="productForm.headings.modifierGroup"
						values={{ product: productDetails?.name }}
					/>
				</h2>
				<div>
					<Formik
						initialValues={{
							searchQuery: query.searchQuery,
							onlySelectedModifierGroups: false,
						}}
						onSubmit={() => {}}
					>
						{() => {
							return (
								<StyledFilterForm>
									<Field
										component={StyledCheckbox}
										name="onlySelectedModifierGroups"
										label={intl.formatMessage({
											id:
												'form.fields.onlySelectedModifierGroups.label',
										})}
										handleChangeEvent={(value: boolean) => {
											setShowSelected(value);
										}}
									/>
									<Field
										component={StyledInput}
										name="searchQuery"
										placeholder={intl.formatMessage({
											id: 'form.search.placeholder',
										})}
										icon="search"
										iconWidth={12}
										iconHeight={12}
										handleChangeEvent={(value: string) =>
											setSearchQuery(value)}
									/>
								</StyledFilterForm>
							);
						}}
					</Formik>
				</div>
			</StyledHeader>
			<Formik
				enableReinitialize
				initialValues={initialValues!}
				onSubmit={handleSubmit}
			>
				{({ dirty, values, isSubmitting }) => {
					const visibleModifierGroups =
						!showSelected && values
							? modifierGroups.sort((a, b) => {
								const aSelected = values[a.id] === true;
								const bSelected = values[b.id] === true;

								if (!aSelected && !bSelected) {
									return 0;
								}

								return bSelected && !aSelected ? 1 : -1;
							  })
							: modifierGroups.filter((modifierGroup) =>
								values
									? values[modifierGroup.id] === true
									: true
							  );

					/** list items */
					const modifierGroupItem = ({
						key,
						index,
						style,
					}: IListItemProps) => (
						<div key={key} style={style}>
							<ProductModifierGroupListItem
								item={visibleModifierGroups[index]}
							/>
						</div>
					);

					return (
						<StyledForm>
							<Prompt
								when={dirty && !formSubmission}
								message=""
							/>
							<StyledHeadings>
								<StyledHeading />
								<StyledHeading
									className="mod-sort"
									onClick={() => setSort('title')}
									onKeyPress={() => setSort('title')}
									role="button"
									tabIndex={0}
								>
									<StyledSortIcons
										active={sortColumn === 'title'}
										sort={sortDirection}
									/>
									<FormattedMessage id="productModifierGroups.headings.title" />
								</StyledHeading>
								<StyledHeading
									className="mod-sort"
									onClick={() => setSort('sku')}
									onKeyPress={() => setSort('sku')}
									role="button"
									tabIndex={0}
								>
									<StyledSortIcons
										active={sortColumn === 'sku'}
										sort={sortDirection}
									/>
									<FormattedMessage id="productModifierGroups.headings.reference" />
								</StyledHeading>
							</StyledHeadings>
							<StyledList>
								{visibleModifierGroups?.length > 0 ? (
									<AutoSizer>
										{({ height, width }) => (
											<List
												width={width || 400}
												height={height || 550}
												rowCount={
													visibleModifierGroups.length
												}
												rowHeight={60}
												rowRenderer={modifierGroupItem}
											/>
										)}
									</AutoSizer>
								) : (
									// TODO - Handle empty list
									''
								)}
							</StyledList>
							<StyledActions>
								<div>
									<Link
										to={`/menus/products?activeAccount=${activeAccountId}`}
									>
										<Button variant="secondary">
											<FormattedMessage id="form.button.back" />
										</Button>
									</Link>
								</div>
								<Button
									type="submit"
									disabled={isSubmitting}
									ariaLabel="submit-button"
								>
									<FormattedMessage id="form.button.save" />
								</Button>
							</StyledActions>
						</StyledForm>
					);
				}}
			</Formik>
		</>
	);
};

export default ProductModifierGroupsForm;
